import { useMemo, useState } from "react";
import { IAnexo } from "../../../../../utility/IAnexo";
import { Container } from "./styles";
import { formatByteSize } from "../../../../../utility/byteSizeUtils";
import { DotIcon, Download, File, FileText, FileSpreadsheet, FileImage, FileArchive, FilePlay, FileType, FileVideoCamera, Loader2, Trash2 } from "lucide-react";
import { comum } from "../../../../../styles/Themes/light";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import api from "../../../../../services/api";
import { IconClock } from "../../../../icons/IconesSistema";
import { IconClose } from "../../../../icons";
import toastservice from "../../../../../services/toast";
import toast from "react-hot-toast";
import { toastDefsError } from "../../../../../config/toastConfig";
import ModalDefault from "../../../../ModalDefault";
import { useAuth } from "../../../../../contexts/UserContext";
import ButtonDefault from "../../../../form/ButtonDefault";
import { ModalWrapper, ModalTitle, ModalButtons } from "../../../../../pages/Demanda/styles";

function mapearTipoResponsavel(tipo?: string): string | null {
    if (!tipo) {
        return null;
    }
    
    const tipoLower = tipo.toLowerCase();
    
    switch (tipoLower) {
        case 'operador':
            return 'Operador';
        case 'solicitante':
            return 'Solicitante';
        case 'usuario':
        case 'profissional':
            return 'Profissional';
        default:
            return tipo;
    }
}

interface CardAnexoProps {
    anexo: IAnexo;
    highlighted?: boolean;
    id_proposta?: number;
    onExcluir?: () => void;
}

export default function CardAnexo({ anexo, highlighted = false, id_proposta, onExcluir }: CardAnexoProps) {

    const { user } = useAuth();
    const [downloading, setDownloading] = useState(false);
    const [mostrarModalConfirmacao, setMostrarModalConfirmacao] = useState(false);
    const [excluindo, setExcluindo] = useState(false);
    
    const normalizeFilePath = (caminho: string, idProposta?: number | null): string => {
        if (!caminho) return caminho;
        if (caminho.includes('/')) {
            return caminho;
        }
        if (idProposta) {
            return `${idProposta}/${caminho}`;
        }
        return caminho;
    };
    
    const caminhoNormalizado = anexo.caminho ? normalizeFilePath(anexo.caminho, anexo.id_proposta) : '';
    
    const nome = useMemo(() => {
        if (caminhoNormalizado) {
            return caminhoNormalizado.split('/').pop() || '';
        }
        return anexo.caminho?.split('/').pop() || '';
    }, [anexo.caminho, caminhoNormalizado]);

    const tamanho = useMemo(() => {
        return formatByteSize(anexo.tamanho || 0);
    }, [anexo.tamanho]);

    const extensao = useMemo(() => {
        return nome.split('.').pop()?.toUpperCase() || '';
    }, [nome]);

    const icone = useMemo(() => {
        const ext = extensao.toLowerCase();
        
        // RN19: PDF - ícone vermelho
        if (ext === 'pdf') {
            return <FileText color={comum.colors.primaryRed400} />;
        }
        
        // RN20: Documentos - ícone azul (file-text)
        if (['doc', 'docx', 'rtf', 'odt', 'txt', 'pages'].includes(ext)) {
            return <FileType color={comum.colors.primaryBlue400} />;
        }
        
        // RN21: Planilhas - ícone verde (file-spreadsheet)
        if (['xls', 'xlsx', 'xlsm', 'xltx', 'ods', 'csv', 'tsv'].includes(ext)) {
            return <FileSpreadsheet color={comum.colors.secondaryGreen400} />;
        }
        
        // RN22: Imagens - ícone azul-claro (file-image)
        if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'tiff', 'tif', 'svg', 'heic', 'heif'].includes(ext)) {
            return <FileImage color={comum.colors.secondaryPool400} />;
        }
        
        // RN23: Arquivos compactados - ícone amarelo (file-archive)
        if (['zip', 'rar', '7z', 'tar', 'gz', 'bz2', 'xz', 'tgz', 'tbz'].includes(ext)) {
            return <FileArchive color={comum.colors.primaryOrange500} />;
        }
        
        // RN24: Áudio - ícone roxo (file-play)
        if (['mp3', 'wav', 'aac', 'm4a', 'ogg', 'flac', 'wma'].includes(ext)) {
            return <FilePlay color={comum.colors.primaryBlue500} />;
        }
        
        // RN25: Vídeo - ícone laranja (file-video-camera)
        if (['mp4', 'avi', 'mov', 'mkv', 'wmv', 'webm', 'm4v'].includes(ext)) {
            return <FileVideoCamera color={comum.colors.primaryOrange400} />;
        }
        
        // RN26: Outros - ícone cinza (file)
        return <File color={comum.colors.interfaceGray400} />;
    }, [extensao]);

    const podeExcluir = user?.profile === 'admin' || user?.profile === 'operador';
    const anexoVinculadoConclusao = anexo.tipo_arquivo === 'arquivo_conclusao';
    const anexoVinculadoAta = anexo.tipo_arquivo === 'ata_processo';
    const anexoBloqueadoExclusao = anexoVinculadoConclusao || anexoVinculadoAta;
    const podeExcluirEsteAnexo = podeExcluir && !anexoBloqueadoExclusao;
    const arquivoExcluido = anexo.excluido === true;

    const handleDownload = async (caminho?: string) => {
        if(downloading || !caminho || arquivoExcluido) {
            if (arquivoExcluido) {
                toastservice.error('Este arquivo foi removido e não está mais disponível.');
            }
            return;
        }
        
        const caminhoNormalizado = normalizeFilePath(caminho, anexo.id_proposta);
        
        setDownloading(true);
        try {
            const pathSegments = caminhoNormalizado.split('/').map(segment => encodeURIComponent(segment));
            await api.download('getfile/' + pathSegments.join('/'), nome);
        } catch (error: any) {
            console.error('Erro ao fazer download:', error);
            if (error.response?.status === 404) {
                toast.error('O arquivo não se encontra no sistema, entre em contato com o operador', {
                    ...toastDefsError,
                    duration: 5000,
                });
            } else {
                toastservice.error('Erro ao baixar o arquivo. Por favor, tente novamente mais tarde.');
            }
        } finally {
            setDownloading(false);
        }
    }

    const handleExcluir = async () => {
        if (!id_proposta || !anexo.id_proposta_arquivo) {
            return;
        }

        const arquivoDeOrientacao = anexo.acao === 'ORIENTACAO';

        setExcluindo(true);
        try {
            let response;
            if (arquivoDeOrientacao && anexo.id_orientacao) {
                response = await api.delete(`orientacao/${anexo.id_orientacao}/arquivos/${anexo.id_proposta_arquivo}`);
            } else {
                response = await api.delete(`proposta/${id_proposta}/anexos/${anexo.id_proposta_arquivo}`);
            }
            const mensagem = response?.data?.message || 'Anexo excluído com sucesso';
            toastservice.success(mensagem);
            setMostrarModalConfirmacao(false);
            onExcluir?.();
        } catch (error: any) {
            console.error('Erro ao excluir anexo:', error);
            if (error.response?.status === 404) {
                toast.error('O arquivo não se encontra no sistema, entre em contato com o operador', {
                    ...toastDefsError,
                    duration: 5000,
                });
            } else {
                const mensagem = error.response?.data?.message || error.message || 'Erro ao excluir anexo';
                toastservice.error(mensagem);
            }
        } finally {
            setExcluindo(false);
        }
    }

    const tipoAnexo = extensao || anexo.tipo_arquivo || 'Arquivo';

    return (
        <Container $highlighted={highlighted} data-anexo-id={anexo.id_proposta_arquivo} style={{ opacity: arquivoExcluido ? 0.75 : 1 }}>
            <div className="icone">
                {icone}
            </div>
            <div className="meio">
                <div className="top">
                    <div className="top-left">
                        <div className="nome" style={{ fontStyle: arquivoExcluido ? 'italic' : 'normal' }}>
                            {nome}
                        </div>
                        {tamanho && (
                            <>
                                <DotIcon size={16}/>
                                <div className="tamanho">{tamanho}</div>
                            </>
                        )}
                    </div>
                </div>
                <div className="bottom">
                    {arquivoExcluido ? (
                        <div className="enviado" style={{ fontSize: '11px', color: 'var(--c64748b)' }}>
                            Arquivo removido e não está mais disponível
                        </div>
                    ) : (
                        <div className="enviado">
                            {anexo.nome_responsavel ? `Enviado por: ${anexo.nome_responsavel}` : 'Enviado por: Sistema'}
                            {mapearTipoResponsavel(anexo.tipo_responsavel) && (
                                <div className="tipo_responsavel">
                                    {mapearTipoResponsavel(anexo.tipo_responsavel)}
                                </div>
                            )}
                        </div>
                    )}
                </div>
                {anexo.origem && !arquivoExcluido && (
                    <div className="origem-container">
                        <span className="origem-label">Origem:</span>
                        <span 
                            className="origem-chip" 
                            title={anexo.origem === 'Não identificada (legado)' 
                                ? 'Este anexo foi criado antes da padronização de origem. Nem todos os dados puderam ser recuperados.' 
                                : anexo.origem}
                        >
                            {anexo.origem}
                        </span>
                    </div>
                )}
            </div>
            <div className="right">
                {anexo.criado && !arquivoExcluido && (
                    <div className="data-right">
                        <IconClock size={14} />
                        {formatDistanceToNow(new Date(anexo.criado), { addSuffix: true, locale: ptBR })}
                    </div>
                )}
                {arquivoExcluido ? (
                    <div style={{
                        padding: '2px 8px',
                        borderRadius: '12px',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        fontSize: '10px',
                        fontWeight: 500,
                        color: 'var(--cef4444)',
                        whiteSpace: 'nowrap'
                    }}>
                        Removido
                    </div>
                ) : (
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <div className="download" onClick={() => handleDownload(anexo.caminho)}>
                            {downloading ? <Loader2 size={20} color={comum.colors.interfaceGray400} className="spinner" /> : <Download size={20} color={comum.colors.interfaceGray400} />}
                        </div>
                        {podeExcluir && (
                            <div
                                className="download"
                                onClick={() => {
                                    if (podeExcluirEsteAnexo) {
                                        setMostrarModalConfirmacao(true);
                                    }
                                }}
                                title={anexoBloqueadoExclusao ? "Não é possível excluir arquivos da Ata do Processo ou da Conclusão da demanda" : "Excluir anexo"}
                                style={{ cursor: podeExcluirEsteAnexo ? 'pointer' : 'not-allowed', opacity: podeExcluirEsteAnexo ? 1 : 0.4 }}
                            >
                                {excluindo ? <Loader2 size={20} color={comum.colors.primaryRed400} className="spinner" /> : <Trash2 size={20} color={comum.colors.primaryRed400} />}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <ModalDefault
                id="modal-confirmacao-exclusao-anexo"
                onClose={() => setMostrarModalConfirmacao(false)}
                openModal={mostrarModalConfirmacao}
                width="520px"
                maxWidth="95vw"
            >
                <ModalWrapper>
                    <ModalTitle>
                        <div className="points">
                            Confirmar Exclusão de Anexo
                        </div>
                        <div
                            className="close"
                            onClick={() => setMostrarModalConfirmacao(false)}
                        >
                            <IconClose />
                        </div>
                    </ModalTitle>

                    <div style={{ width: '100%', height: '1px', backgroundColor: comum.colors.interfaceBorder100 || '#E5E7EB' }} />

                    <div className="accept">
                        <p>
                            <strong>Nome do arquivo:</strong> {nome}
                        </p>
                        <p>
                            <strong>Tipo de anexo:</strong> {tipoAnexo}
                        </p>
                        {anexo.origem && (
                            <p>
                                <strong>Origem:</strong> {anexo.origem}
                            </p>
                        )}
                        <p style={{ marginTop: '16px' }}>
                            Deseja confirmar a exclusão deste anexo da demanda? Esta ação é irreversível.
                        </p>
                    </div>

                    <ModalButtons>
                        <ButtonDefault
                            className="cancel"
                            onClick={() => setMostrarModalConfirmacao(false)}
                            disabled={excluindo}
                        >
                            Cancelar
                        </ButtonDefault>
                        <ButtonDefault
                            className="danger"
                            onClick={handleExcluir}
                            disabled={excluindo}
                        >
                            {excluindo ? 'Excluindo...' : 'Confirmar Exclusão'}
                        </ButtonDefault>
                    </ModalButtons>
                </ModalWrapper>
            </ModalDefault>
        </Container>
    );
}